import { reduxForm } from "redux-form";

import { Card, Dialog } from "widgets";

import style from "@/modules/files/styles/FileBrowserActions.module.scss";

import { simpleFieldValidation } from "../../../../app-utils/form.js";
import { type IConfiguredPhenotype } from "../../../../redux/modules/genome-browser.js";
import { type ReduxDispatch } from "../../../../redux/redux-utils.js";
import { type IPopoverPropsInjectedFromController } from "../../../core/overlays/components/OverlayControllers.js";
import {
  ColorSchemeConfigurationFields,
  type ColorSchemeFormFields,
  type DefaultColorSchemeFormData,
} from "../../ColorSchemeConfigurationFields.js";

// This is a generic component which is not responsible of initializing the values on applying any changes
interface IPhenotypeInputConfigurationDialogProps extends IPopoverPropsInjectedFromController {
  phenotype: IConfiguredPhenotype;
  onApply: (configuredPhenotype: IConfiguredPhenotype) => void;
}

// These are the form inputs fields name. The keys for the values from the form.
enum COLOR_SCHEME_FORM_FIELDS_NAME {
  SCALE = "scale",
  MIN = "min",
  MAX = "max",
  LOG = "log",
}

interface ColorSchemeFormData {
  [COLOR_SCHEME_FORM_FIELDS_NAME.SCALE]: DefaultColorSchemeFormData["SCALE"];
  [COLOR_SCHEME_FORM_FIELDS_NAME.MIN]: DefaultColorSchemeFormData["MIN"];
  [COLOR_SCHEME_FORM_FIELDS_NAME.MAX]: DefaultColorSchemeFormData["MAX"];
  [COLOR_SCHEME_FORM_FIELDS_NAME.LOG]: DefaultColorSchemeFormData["LOG"];
}

const validate = (values: ColorSchemeFormData) => {
  const min = Number.parseFloat(values[COLOR_SCHEME_FORM_FIELDS_NAME.MIN]);
  const max = Number.parseFloat(values[COLOR_SCHEME_FORM_FIELDS_NAME.MAX]);

  return min >= max
    ? {
        _error: "The min value must be smaller than the max value",
      }
    : {};
};

const submit = (
  values: ColorSchemeFormData,
  _dispatch: ReduxDispatch,
  props: IPhenotypeInputConfigurationDialogProps,
) => {
  const min = values[COLOR_SCHEME_FORM_FIELDS_NAME.MIN];
  const max = values[COLOR_SCHEME_FORM_FIELDS_NAME.MAX];
  const logScale = values[COLOR_SCHEME_FORM_FIELDS_NAME.LOG];

  const phenotype: IConfiguredPhenotype = {
    ...props.phenotype,
    colorScheme: values[COLOR_SCHEME_FORM_FIELDS_NAME.SCALE],
    minValue: min ? Number.parseFloat(min) : props.phenotype.minValue,
    maxValue: max ? Number.parseFloat(max) : props.phenotype.maxValue,
    logScale,
  };

  props.onApply(phenotype);
};

const ColorSchemeConfigurationDialogForm = reduxForm<ColorSchemeFormData, IPhenotypeInputConfigurationDialogProps>({
  form: "PHENOTYPE_INPUT_CONFIGURATION",
  validate,
})((props) => {
  const colorSchemeFormFields: ColorSchemeFormFields = {
    SCALE: { name: COLOR_SCHEME_FORM_FIELDS_NAME.SCALE },
    MIN: { name: COLOR_SCHEME_FORM_FIELDS_NAME.MIN, validate: simpleFieldValidation },
    MAX: { name: COLOR_SCHEME_FORM_FIELDS_NAME.MAX, validate: simpleFieldValidation },
    LOG: { name: COLOR_SCHEME_FORM_FIELDS_NAME.LOG },
  };

  return (
    <Dialog
      {...props}
      ref={props.dialogRef}
      title={"Customize Phenotype Color"}
      mainButton={{
        isLoading: props.submitting,
        label: "Apply",
        onPress: props.handleSubmit(submit),
      }}
      errorCallout={{ message: props.error }}
    >
      <Card className={style.info_card_for_dialog} scrollable>
        <ColorSchemeConfigurationFields fields={colorSchemeFormFields} />
      </Card>
    </Dialog>
  );
});

export { ColorSchemeConfigurationDialogForm };
